// ui-kit: hand-built
import {
    type ComponentPropsWithRef,
    type KeyboardEvent,
    type KeyboardEventHandler,
    type MouseEvent,
    type ReactNode,
    type Ref,
} from 'react';
import { RiArrowRightSLine, RiCheckLine } from '@remixicon/react';

import { classNames } from '../../internal/classNames';
import { useLinkComponent } from '../../internal/LinkProvider';

import Checkbox from '../Checkbox/Checkbox';
import { ChipTag, type ChipColor } from '../Chip/Chip';
import Icon, { type IconSource } from '../Icon/Icon';

/** Figma Menu (refactor) typologies — Slider omitted (not in use). */
export type MenuItemTypology = 'link' | 'select' | 'selectTag' | 'checkbox' | 'action';

type MenuItemShared = {
    typology?: MenuItemTypology;
    /** Row label (text, or ignored when `children` supplies the label). */
    label?: ReactNode;
    children?: ReactNode;
    /** Leading icon for `link` and `action`. */
    icon?: IconSource;
    /** Trailing check for `select` / `selectTag`, or checked state for `checkbox`. */
    selected?: boolean;
    /** Chip color for `selectTag`. */
    tagColor?: ChipColor;
    /** Negative styling for destructive `action` rows. */
    destructive?: boolean;
    /** Same highlight as CSS `:hover` (used by react-select hosts via `isFocused`). */
    focused?: boolean;
    disabled?: boolean;
};

export type MenuItemDivProps = MenuItemShared &
    Omit<ComponentPropsWithRef<'div'>, 'children'> & {
        href?: undefined;
        ref?: Ref<HTMLDivElement>;
    };

/** Renders through the host `LinkProvider` component; disabled links render a native `<a>` without `href`. */
export type MenuItemLinkProps = MenuItemShared &
    Omit<ComponentPropsWithRef<'a'>, 'children'> & {
        href: string;
        ref?: Ref<HTMLAnchorElement>;
    };

export type MenuItemProps = MenuItemDivProps | MenuItemLinkProps;

const MenuItem = (props: MenuItemProps) => {
    const Link = useLinkComponent();
    const {
        typology = 'select',
        label,
        children,
        icon,
        selected = false,
        tagColor = 'yellow',
        destructive = false,
        focused = false,
        disabled = false,
        className = '',
        tabIndex = disabled ? undefined : -1,
        onKeyDown,
        ...rest
    } = props;
    const content = children ?? label;
    const isLink = 'href' in props && props.href !== undefined;

    const rootProps = {
        tabIndex,
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
            (onKeyDown as KeyboardEventHandler<HTMLElement> | undefined)?.(e);
            if (isLink || e.defaultPrevented || (e.key !== 'Enter' && e.key !== ' ')) {
                return;
            }
            e.preventDefault();
            e.currentTarget.click();
        },
        className: classNames({
            'ui-menu-item': true,
            [`ui-menu-item--${typology}`]: true,
            'ui-menu-item--selected': selected,
            'ui-menu-item--focused': focused,
            'ui-menu-item--disabled': disabled,
            'ui-menu-item--destructive': destructive && typology === 'action',
            [className]: Boolean(className),
        }),
        'data-test-component': 'ui-kit/MenuItem',
        'aria-disabled': disabled || undefined,
        'aria-selected':
            typology === 'select' || typology === 'selectTag' || typology === 'checkbox'
                ? selected
                : undefined,
    };

    const body = (
        <>
            {typology === 'link' && icon ? (
                <Icon
                    icon={icon}
                    size={16}
                    className='ui-menu-item__leading'
                />
            ) : null}

            {typology === 'checkbox' ? (
                <Checkbox
                    checked={selected}
                    disabled={disabled}
                    tabIndex={-1}
                    className='ui-menu-item__checkbox'
                    aria-hidden
                />
            ) : null}

            {typology === 'action' && icon ? (
                <Icon
                    icon={icon}
                    size={16}
                    className='ui-menu-item__leading'
                />
            ) : null}

            <span className='ui-menu-item__body'>
                {typology === 'selectTag' ? (
                    <ChipTag
                        color={tagColor}
                        size='small'>
                        {content}
                    </ChipTag>
                ) : (
                    <span className='ui-menu-item__label'>{content}</span>
                )}
            </span>

            {typology === 'link' ? (
                <Icon
                    icon={RiArrowRightSLine}
                    size={16}
                    className='ui-menu-item__trailing ui-menu-item__trailing--chevron'
                />
            ) : null}

            {(typology === 'select' || typology === 'selectTag') && selected ? (
                <Icon
                    icon={RiCheckLine}
                    size={16}
                    className='ui-menu-item__trailing ui-menu-item__trailing--check'
                />
            ) : null}
        </>
    );

    if (isLink) {
        const { href, onClick, ref, ...anchorRest } = rest as Omit<
            MenuItemLinkProps,
            keyof MenuItemShared | 'className' | 'tabIndex' | 'onKeyDown'
        >;

        const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
            if (disabled) {
                e.preventDefault();
                return;
            }
            onClick?.(e);
        };

        return disabled ? (
            <a
                {...anchorRest}
                {...rootProps}
                ref={ref}
                onClick={handleClick}>
                {body}
            </a>
        ) : (
            <Link
                {...anchorRest}
                {...rootProps}
                ref={ref}
                href={href}
                onClick={handleClick}>
                {body}
            </Link>
        );
    }

    const { ref, ...divRest } = rest as Omit<
        MenuItemDivProps,
        keyof MenuItemShared | 'className' | 'tabIndex' | 'onKeyDown'
    >;

    return (
        <div
            {...divRest}
            {...rootProps}
            ref={ref}>
            {body}
        </div>
    );
};

MenuItem.displayName = 'MenuItem';

export default MenuItem;
